import { useEffect, useRef, useState } from "react"
import { AlertCircle, Check, CheckCheck, Clock, PhoneIncoming, PhoneMissed, PhoneOutgoing, Video, Reply, Forward, Star, Trash2, MoreVertical, Pin, Play, Pause, Download, FileText, ExternalLink, Smile, MapPin, User, CheckSquare, Pencil, Info, ImageOff, RefreshCw, Copy, Megaphone } from "lucide-react"
import { UploadRetry, UploadRing } from "@/clientWeb/components/ui/upload-ring"
import { useOutboxEntry, type OutboxEntry } from "./store/upload-outbox.store"
import { cancelFilesMessage, retryFilesMessage, stopFilesMessage } from "./services/upload-outbox"
import type { Message, MessageMedia } from "./types"
import { inviteIdFromHref } from "./invite-link"
import { hasCopyableText } from "./copy-text"
import { downloadAttachment } from "./services/download"
import { useAutoDownload } from "./hooks/useAutoDownload"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import { Popover, PopoverContent, PopoverTrigger } from "@/clientWeb/components/ui/popover"
import { useAttachmentSrc } from "./hooks/useAttachmentSrc"
import { useAttachmentSrcState } from "./hooks/useAttachmentSrcState"
import { AvatarPhoto } from "./avatar-photo"
import { albumLayout } from "./attachments"
import { isEditable } from "./hooks/useEditMessage"
import { usePointerMenu, type PointerMenuState } from "./hooks/usePointerMenu"
import { PointerMenuAnchor } from "./hooks/PointerMenuAnchor"

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"]

/**
 * A picked message in select mode. It used to be an 8% primary tint, which
 * disappears on a photo or coloured chat background, so the user could not
 * tell what they had picked. Same near-opaque backdrop and ring as the
 * reply-jump highlight, readable on any background.
 */
const SELECTED_ROW = "bg-background/85 ring-2 ring-primary shadow-sm rounded-lg px-2 -mx-2"

interface MessageBubbleProps {
  message: Message
  showAvatar?: boolean
  isGroup?: boolean
  isFirst?: boolean
  isLast?: boolean
  onReply?: (message: Message) => void
  onForward?: (message: Message) => void
  onCopy?: (message: Message) => void
  onStar?: (message: Message) => void
  onPin?: (message: Message) => void
  onDelete?: (message: Message) => void
  onEdit?: (message: Message) => void
  onReact?: (message: Message, emoji: string) => void
  onImageClick?: (message: Message) => void
  onFileClick?: (message: Message) => void
  sentBubbleColor?: string
  isSelectMode?: boolean
  isSelected?: boolean
  onSelect?: (id: string) => void
  onSelectFromMenu?: (id: string) => void
  /** Jump to the message this one is replying to. Without it the quoted block
   *  is decoration — it names a message and gives no way to reach it, which in
   *  a long thread is where the reply loses its meaning. */
  onJumpToMessage?: (id: string) => void
  /** Broadcast threads only: open "Message info" (who has it / has read it). */
  onInfo?: (message: Message) => void
  /**
   * A same-origin InMeet group-invite link was clicked (plain left click, no
   * modifier key). The caller owns the actual navigation, so this component
   * never needs a Router in scope: most of the places it renders (menu/photo
   * tests, forwarded-message previews) have none, and a `useNavigate()` here
   * would throw for every one of them. When this is left unset, an invite
   * link just behaves like any other address.
   */
  onInviteLinkClick?: (conversationId: string) => void
}

/**
 * A link in a message, if this token is one.
 *
 * Only http and https become links. A `javascript:` or `data:` token is left as
 * text — it is not something a reader should be one click away from, and this
 * text comes from whoever sent the message.
 *
 * Trailing punctuation is trimmed off the href: people write "see https://x.com."
 * and the full stop belongs to the sentence, not to the address.
 */
export function linkFromToken(token: string): { href: string; label: string; tail: string } | null {
  const m = /^(https?:\/\/[^\s]+?)([.,;:!?)\]]*)$/i.exec(token)
  if (m) {
    const [, href, tail] = m
    try {
      const u = new URL(href)
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
    } catch {
      return null
    }
    return { href, label: href, tail }
  }

  // `www.` with no scheme in front of it. This is what people TYPE, as opposed
  // to what they paste, and it was read as ordinary text — so half of the
  // addresses in a chat were followable and the other half were not, for a
  // reason invisible to whoever wrote them.
  //
  // Bare domains (`example.com`) are deliberately still not linked: nothing
  // separates them from `node.js`, `README.md` or the `e.g.` in a sentence,
  // and turning prose into links is worse than leaving one address plain.
  const w = /^(www\.[^\s]*[^\s.,;:!?)\]])([.,;:!?)\]]*)$/i.exec(token)
  if (!w) return null
  const [, label, tail] = w
  // Needs an actual domain after the `www.` — `www.foo` is not an address.
  if (!/^www\.[^.\s]+\.[^.\s]/i.test(label)) return null
  const href = `https://${label}`
  try {
    if (new URL(href).protocol !== 'https:') return null
  } catch {
    return null
  }
  // The label stays as written; rewriting it to include a scheme the user did
  // not type makes the message read as something they did not send.
  return { href, label, tail }
}

function TextWithMentionChips({
  text,
  isSentByMe,
  onInviteLinkClick,
}: {
  text: string
  isSentByMe: boolean
  onInviteLinkClick?: (conversationId: string) => void
}) {
  const parts = text.split(/(\s+)/)
  return (
    <span className="leading-relaxed">
      {parts.map((p, i) => {
        // A URL was rendered as ordinary text, so a reader could see an address
        // and had no way to follow it: copying it out by hand was the only
        // option. Checked before the mention test: neither pattern can match
        // the other, and a link is the commoner case.
        const link = linkFromToken(p)
        if (link) {
          // An InMeet group invite is the one address this app can actually do
          // something with: hand it to the caller to open in-app, in this tab.
          // Anything else is somebody else's page and keeps behaving like one.
          const inviteId = onInviteLinkClick ? inviteIdFromHref(link.href, window.location.origin) : null
          return (
            <span key={i}>
              <a
                href={link.href}
                target={inviteId ? undefined : "_blank"}
                rel="noopener noreferrer nofollow"
                onClick={(e) => {
                  e.stopPropagation()
                  if (!inviteId) return
                  // Leave a modified click (new tab, new window, download…)
                  // alone: only a plain left click is rerouted in-app.
                  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return
                  e.preventDefault()
                  onInviteLinkClick!(inviteId)
                }}
                className={`underline underline-offset-2 break-all ${
                  isSentByMe ? "text-primary-foreground" : "text-primary"
                }`}
              >
                {link.label}
              </a>
              {link.tail}
            </span>
          )
        }
        const isMention = /^@[\p{L}\p{N}_][\p{L}\p{N}_-]*$/u.test(p)
        if (!isMention) return <span key={i}>{p}</span>
        return (
          <span
            key={i}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-semibold align-baseline ${
              isSentByMe
                ? "bg-primary-foreground/20 text-primary-foreground"
                : "bg-primary/10 text-primary"
            }`}
          >
            {p}
          </span>
        )
      })}
    </span>
  )
}

// `onDark` = rendered on top of the blue "my message" bubble, where the default
// muted-gray / blue tints are near-invisible. Use light-on-blue tints, and a
// bright sky tick for "read" so it clearly pops against the bubble.
function StatusIcon({ status, onDark }: { status: string; onDark?: boolean }) {
  const dim = onDark ? "text-primary-foreground/70" : "text-muted-foreground/70"
  const read = onDark ? "text-sky-300" : "text-sky-500"
  switch (status) {
    case "sending":   return <Clock className={`w-3.5 h-3.5 ${onDark ? "text-primary-foreground/60" : "text-muted-foreground"}`} />
    case "sent":      return <Check className={`w-3.5 h-3.5 ${dim}`} />
    case "delivered": return <CheckCheck className={`w-3.5 h-3.5 ${dim}`} />
    case "read":      return <CheckCheck className={`w-3.5 h-3.5 ${read}`} />
    // A file message whose upload or send failed — see UploadControl. One the
    // user stopped ("stopped") falls through to no mark.
    case "failed":    return <AlertCircle className={`w-3.5 h-3.5 ${onDark ? "text-red-200" : "text-red-500"}`} aria-label="Not sent" />
    default: return null
  }
}

/**
 * The control on a file message still on its way, as WhatsApp draws it: a
 * ring filling with the bytes sent and an ✗ that stops it, a spinner while
 * the stored file's message is sent, and ↑ to send it again once it was
 * stopped or failed. Driven by the message's outbox entry —
 * services/upload-outbox.ts.
 */
function UploadControl({ upload, size = 48, tone = "media" }: { upload: OutboxEntry; size?: number; tone?: "media" | "plain" }) {
  if (upload.state === "failed" || upload.state === "stopped") {
    return <UploadRetry size={size} tone={tone} onRetry={() => retryFilesMessage(upload.messageId)} />
  }
  return (
    <UploadRing
      size={size}
      tone={tone}
      progress={upload.state === "uploading" ? upload.progress : null}
      // Stops, never deletes: the message stays with Retry and Delete, so a
      // stray tap here costs a tap, not the message.
      onCancel={upload.state === "uploading" ? () => stopFilesMessage(upload.messageId) : undefined}
    />
  )
}

/** Under a stopped or failed file message: what happened, Retry and Delete.
 *  Grey when the user stopped it, red when something went wrong. */
function UploadHaltedStrip({ upload }: { upload: OutboxEntry }) {
  const failed = upload.state === "failed"
  return (
    <div className={`mt-0.5 flex max-w-[280px] flex-wrap items-center justify-end gap-x-2 text-[11px] ${failed ? "text-red-500" : "text-muted-foreground"}`}>
      {failed && <AlertCircle className="h-3.5 w-3.5 shrink-0" />}
      <span className="font-medium">{failed ? "Not sent" : "Upload stopped"}</span>
      <button type="button" onClick={() => retryFilesMessage(upload.messageId)} className="font-semibold text-primary hover:underline">
        Retry
      </button>
      <button type="button" onClick={() => cancelFilesMessage(upload.messageId)} className="font-semibold text-muted-foreground hover:underline">
        Delete
      </button>
      {failed && upload.error && <span className="basis-full text-right text-red-500/80">{upload.error}</span>}
    </div>
  )
}

/** UploadControl over a photo, an album or a video: the picture dimmed under
 *  it, and taps kept off the viewer underneath until the file has gone. */
function MediaUploadOverlay({ upload }: { upload: OutboxEntry }) {
  return (
    <div className="absolute inset-0 z-10 grid place-items-center bg-black/30" onClick={e => e.stopPropagation()}>
      <UploadControl upload={upload} />
    </div>
  )
}

/**
 * Time (+ ticks for my own messages) shown INSIDE the bubble, after the text.
 *
 * Inline, NOT floated. `float-right` tucked it into the last line's trailing
 * space, which reads well — but a float is out of flow, so it contributed
 * nothing to the bubble's `w-fit` width and nothing to its height. A one
 * character message sized the bubble to that character and the timestamp then
 * hung outside it entirely; longer messages had the ticks clipped off the
 * bottom by the bubble's `overflow-hidden`.
 *
 * In flow, the bubble measures the text AND this, so it is always wide enough
 * and tall enough to hold what it contains. The cost is that on a message whose
 * last line is short the meta sits just after the text rather than pushed flush
 * right — which is what the float bought, and not worth a timestamp that
 * escapes its bubble.
 */
/** Full local date + time, for the hover title on a bubble's clock face. */
function fullTimestamp(iso?: string): string | undefined {
  if (!iso) return undefined
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleString()
}

function BubbleMeta({ time, createdAt, status, isSentByMe, isStarred, editedAt, viaBroadcast }: { time?: string; createdAt?: string; status?: string; isSentByMe: boolean; isStarred?: boolean; editedAt?: string | null; viaBroadcast?: boolean }) {
  if (!time) return null
  return (
    <span
      // The clock face alone cannot say which day; hovering gives the exact
      // moment, in the reader's own timezone.
      title={fullTimestamp(createdAt)}
      className={`ml-auto inline-flex shrink-0 items-center gap-0.5 text-[10px] leading-none select-none whitespace-nowrap ${
        isSentByMe ? "text-primary-foreground/70" : "text-muted-foreground"
      }`}
    >
      {/* Beside the clock, where every bubble already has room.
          
          There was a star, pinned to the bubble's outside corner — and every
          bubble variant that is not a rich card carries `overflow-hidden`, so
          it was clipped away the moment it was drawn. Starring a message
          changed nothing you could see, which is the same as not starring it. */}
      {isStarred && <Star className="w-3 h-3 fill-amber-400 text-amber-400" aria-label="Starred" />}
      {/* The text of a message can change after it is sent — anyone in the
          thread may edit it — and until this marker existed the reader had no
          way to tell that it had. Sits before the clock, as in every other
          chat client, and carries the edit time on hover. */}
      {editedAt && (
        <span title={`Edited ${fullTimestamp(editedAt) ?? ""}`.trim()} className="italic opacity-80">
          Edited
        </span>
      )}
      {/* WhatsApp's broadcast mark: this went out to a list, and this DM got
          its own copy. Only the sender ever sees it — the recipient's copy is
          an ordinary message (`viaBroadcast` is never set on their side). */}
      {/* Tick-sized, heavier and at full strength: at the meta row's 12px and
          70% tint its thin outline all but vanished on the blue bubble. */}
      {viaBroadcast && isSentByMe && (
        <span title="Sent via broadcast" className="inline-flex">
          <Megaphone className="w-3.5 h-3.5 text-primary-foreground" strokeWidth={2.5} aria-label="Sent via broadcast" />
        </span>
      )}
      {time}
      {isSentByMe && <StatusIcon status={status ?? "sent"} onDark />}
    </span>
  )
}

export function Avatar({ name, imageUrl, contactId, size = 28 }: { name: string; imageUrl?: string; contactId?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const colorIdx = name.charCodeAt(0) % colors.length
  return (
    <div
      className={`rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 overflow-hidden ${colors[colorIdx]}`}
      style={{ width: size, height: size }}
    >
      <AvatarPhoto src={imageUrl} alt={name} className="w-full h-full object-cover" fallback={initials} contactId={contactId} />
    </div>
  )
}

function formatFileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** "1.2 MB / 4.8 MB" while a file uploads, its plain size otherwise. */
function uploadSizeLabel(size: number, upload?: OutboxEntry): string {
  if (!upload || upload.state !== "uploading") return formatFileSize(size)
  return `${formatFileSize(Math.round(size * upload.progress))} / ${formatFileSize(size)}`
}

/**
 * One box size for a single photo message, shared by the placeholder, the
 * loading spinner and the loaded `<img>`. The loaded image sits in a 260px
 * column at `w-full h-auto max-h-52` (52 * 4 = 208px cap), so this mirrors
 * that: width is always 260, height is the image's own ratio clamped to
 * max-h-52 (208), floored at 80 so a panorama doesn't collapse to a sliver.
 * Without known dimensions we fall back to a fixed 160, same as before.
 * Keeping all three states the same box is what stops the bubble from
 * resizing as a received photo goes placeholder -> spinner -> image.
 */
export function singlePhotoBox(width?: number | null, height?: number | null): { width: number; height: number } {
  if (!width || !height || width <= 0 || height <= 0) return { width: 260, height: 160 }
  const ratio = width / height
  const height260 = Math.round(Math.min(208, 260 / ratio))
  return { width: 260, height: Math.max(80, height260) }
}

/**
 * Stand-in for media the user has not chosen to download yet.
 *
 * Auto-download off must never hide that something was sent — the size and a
 * one-tap control are the whole point, so the recipient can decide before
 * spending the bandwidth. Reserves the real aspect ratio when we know it, so
 * loading the media doesn't shift the thread.
 */
function MediaPlaceholder({
  label,
  size,
  width,
  height,
  preview,
  onLoad,
  isSentByMe,
  box,
}: {
  label: string
  size?: number
  width?: number | null
  height?: number | null
  /** Small preview image (thumbnailUrl) shown blurred behind the control, so
   *  the recipient can see WHAT arrived before paying for the full download. */
  preview?: string
  onLoad: () => void
  isSentByMe: boolean
  /** Caller-supplied box (single photo uses `singlePhotoBox` so the
   *  placeholder, spinner and loaded image never change size). When absent,
   *  falls back to this component's own ratio-based sizing, unchanged. */
  box?: { width: number; height: number }
}) {
  const ratio = width && height ? width / height : undefined
  const boxStyle = box
    ? box
    : ratio
      ? { width: 260, height: Math.round(Math.min(320, Math.max(120, 260 / ratio))) }
      : { width: 260, height: 160 }

  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); onLoad() }}
      style={boxStyle}
      className={`group relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl transition-colors ${
        preview
          ? "border-0"
          : isSentByMe
            ? "border border-dashed border-primary-foreground/30 bg-primary-foreground/10 hover:bg-primary-foreground/20"
            : "border border-dashed border-border bg-muted/50 hover:bg-muted"
      }`}
    >
      {preview && (
        <>
          <img
            src={preview}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full scale-110 object-cover blur-md"
          />
          {/* Scrim: keeps the label legible over a bright or busy image. */}
          <span className="absolute inset-0 bg-black/35" />
        </>
      )}
      <span className={`relative grid h-10 w-10 place-items-center rounded-full ${preview ? "bg-black/45 backdrop-blur-sm" : isSentByMe ? "bg-primary-foreground/20" : "bg-background/80"}`}>
        <Download className={`h-4 w-4 ${preview ? "text-white" : isSentByMe ? "text-primary-foreground" : "text-foreground"}`} />
      </span>
      <span className={`relative text-xs font-medium ${preview ? "text-white" : isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>
        {label}
      </span>
      {size ? (
        <span className={`relative text-[11px] ${preview ? "text-white/80" : isSentByMe ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
          {formatFileSize(size)}
        </span>
      ) : null}
    </button>
  )
}

/**
 * Stand-in for a photo that did not load: either the authed fetch behind
 * `useAttachmentSrcState` failed, or a plain URL's `<img>` fired `onError`
 * (a 404, an expired signed link, and so on). Both used to render the same
 * spinner forever, with nothing on screen to say the photo was never coming
 * and no way to try again short of reloading the whole thread.
 *
 * Same box as `MediaPlaceholder` and the loading spinner, so this is the
 * third state sharing one size rather than a fourth one the bubble has to
 * resize around.
 */
function FailedPhotoBox({
  box,
  isSentByMe,
  onRetry,
}: {
  box: { width: number; height: number }
  isSentByMe: boolean
  onRetry: () => void
}) {
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); onRetry() }}
      style={box}
      className={`flex flex-col items-center justify-center gap-1.5 ${isSentByMe ? "bg-primary-foreground/10" : "bg-muted/50"}`}
    >
      <ImageOff className={`h-5 w-5 ${isSentByMe ? "text-primary-foreground/60" : "text-muted-foreground"}`} />
      <span className={`text-xs ${isSentByMe ? "text-primary-foreground/80" : "text-foreground"}`}>
        Couldn't load photo
      </span>
      <span className={`flex items-center gap-1 text-[11px] ${isSentByMe ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
        <RefreshCw className="h-3 w-3" /> Tap to retry
      </span>
    </button>
  )
}

function AudioBubble({
  url: rawUrl,
  duration,
  isSentByMe,
  time,
  createdAt,
  status,
  isStarred,
  editedAt,
  viaBroadcast,
  upload,
}: {
  editedAt?: string | null
  isStarred?: boolean
  viaBroadcast?: boolean
  /** Still uploading: the ring takes the play button's place. */
  upload?: OutboxEntry
  url?: string
  duration?: number
  isSentByMe: boolean
  /** Sent time and delivery tick, as every other message shows them. A voice
   *  note had neither: the meta row is built at the end of the text branch and
   *  this bubble returns long before reaching it. */
  time?: string
  createdAt?: string
  status?: string
}) {
  const url = useAttachmentSrc(rawUrl)
  const { shouldLoad, load } = useAutoDownload("audio", rawUrl, isSentByMe)
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const bars = [0.3, 0.5, 0.8, 0.4, 1, 0.6, 0.3, 0.9, 0.5, 0.4, 0.7, 1, 0.8, 0.4, 0.6, 0.9, 0.3, 0.7, 0.5, 0.8, 0.4, 0.6, 0.3, 0.7]
  const fmt = (s = 0) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`

  // Duration can come from the sender's metadata; fall back to whatever the
  // element reports once it has loaded.
  const [loadedDuration, setLoadedDuration] = useState<number | undefined>(undefined)
  const total = duration ?? loadedDuration
  const progress = total && total > 0 ? Math.min(1, elapsed / total) : 0

  const toggle = () => {
    const el = audioRef.current
    if (!el || !url) return
    if (el.paused) {
      void el.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
    } else {
      el.pause()
      setPlaying(false)
    }
  }

  // `preload="metadata"` still hits the network, so honour the toggle before
  // the element exists rather than after.
  if (!shouldLoad) {
    return (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); load() }}
        className={`flex items-center gap-2.5 min-w-[180px] py-1 px-1 rounded-lg transition-colors ${isSentByMe ? "hover:bg-primary-foreground/10" : "hover:bg-muted"}`}
      >
        <span className={`grid h-8 w-8 place-items-center rounded-full shrink-0 ${isSentByMe ? "bg-primary-foreground/20" : "bg-primary/15"}`}>
          <Download className="h-3.5 w-3.5" />
        </span>
        <span className={`text-xs ${isSentByMe ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
          Tap to load audio
        </span>
        <span className="ml-auto pl-2">
          <BubbleMeta time={time} createdAt={createdAt} status={status} isSentByMe={isSentByMe} isStarred={isStarred} editedAt={editedAt} viaBroadcast={viaBroadcast} />
        </span>
      </button>
    )
  }

  return (
    <div className="flex items-center gap-2.5 min-w-[180px] py-0.5">
      {url && (
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          onTimeUpdate={e => setElapsed(Math.floor(e.currentTarget.currentTime))}
          onLoadedMetadata={e => {
            const d = e.currentTarget.duration
            if (Number.isFinite(d)) setLoadedDuration(Math.round(d))
          }}
          onEnded={() => { setPlaying(false); setElapsed(0) }}
          onPause={() => setPlaying(false)}
        />
      )}
      {upload ? (
        <UploadControl upload={upload} size={32} tone="plain" />
      ) : (
        <button
          onClick={toggle}
          disabled={!url}
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors disabled:opacity-50 ${isSentByMe ? "bg-primary-foreground/20 hover:bg-primary-foreground/30" : "bg-primary/15 hover:bg-primary/25"}`}
        >
          {playing
            ? <Pause className="w-3.5 h-3.5" />
            : <Play className="w-3.5 h-3.5 ml-0.5" />
          }
        </button>
      )}
      <div className="flex items-end gap-[2px] h-5 flex-1">
        {bars.map((h, i) => {
          const played = i / bars.length <= progress
          const activeBar = isSentByMe ? "bg-primary-foreground" : "bg-primary"
          const idleBar = isSentByMe ? "bg-primary-foreground/50" : "bg-primary/50"
          return (
            <div
              key={i}
              className={`w-[2px] rounded-full transition-colors ${played ? activeBar : idleBar}`}
              style={{ height: `${h * 100}%` }}
            />
          )
        })}
      </div>
      <span className={`text-[10px] font-mono shrink-0 ${isSentByMe ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
        {fmt(playing || elapsed > 0 ? elapsed : total)}
      </span>
      <BubbleMeta time={time} createdAt={createdAt} status={status} isSentByMe={isSentByMe} isStarred={isStarred} editedAt={editedAt} viaBroadcast={viaBroadcast} />
    </div>
  )
}

function VideoBubble({ url: rawUrl, name, size, poster: rawPoster, isSentByMe, onOpen, upload }: { url?: string; name?: string; size?: number; poster?: string; isSentByMe: boolean; onOpen?: () => void; upload?: OutboxEntry }) {
  // Both go through the resolver: on the local storage backend they are
  // bearer-authed URLs a media element cannot load on its own.
  const url = useAttachmentSrc(rawUrl)
  const poster = useAttachmentSrc(rawPoster)
  const { shouldLoad, load } = useAutoDownload("video", rawUrl, isSentByMe)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)

  const play = () => {
    setPlaying(true)
    void videoRef.current?.play().catch(() => setPlaying(false))
  }

  // `preload="metadata"` and the poster both fetch, so gate before rendering.
  if (!shouldLoad) {
    return <MediaPlaceholder label="Video" size={size} preview={poster} onLoad={load} isSentByMe={isSentByMe} />
  }

  return (
    <div>
      {/* Edge to edge, as the photo bubble is. The bubble around this supplies
          the rounding and clips to it, so a second radius here drew a rounded
          box inside a rounded box with a sliver of bubble showing at each
          corner. */}
      <div className="relative overflow-hidden max-w-[260px] bg-black">
        <video
          ref={videoRef}
          src={url}
          poster={poster}
          preload="metadata"
          className="w-full h-auto max-h-52 object-contain"
          controls={playing}
          onEnded={() => setPlaying(false)}
        />
        {upload ? (
          <>
            <MediaUploadOverlay upload={upload} />
            {size && (
              <span className="absolute bottom-2 left-2 z-10 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full tabular-nums">
                {uploadSizeLabel(size, upload)}
              </span>
            )}
          </>
        ) : !playing && (
          <>
            {/* Opens the full-screen viewer, the same as tapping a photo.
                It used to start the video inline instead, so the same gesture
                did two different things depending on what you had tapped. */}
            <button type="button" onClick={() => (onOpen ? onOpen() : play())} className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Play className="w-6 h-6 text-white fill-white ml-0.5" />
              </div>
            </button>
            {/* No download in the corner. Tapping the tile opens the viewer
                and the viewer has one, so this was a second control for the
                same thing — sitting on top of the media it was showing. */}
            {size && (
              <span className="absolute bottom-2 left-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full">
                {formatFileSize(size)}
              </span>
            )}
          </>
        )}
      </div>
      {/* Same inset as every other card's content — see the caption. */}
      {name && <p className={`text-[10px] px-3 pt-1 pb-0.5 truncate ${isSentByMe ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{name}</p>}
    </div>
  )
}

interface FileBubbleProps {
  /** Sent time, as every other message shows it. A document had none: the meta
   *  row lives in the text branch, and this card returns before reaching it, so
   *  a shared file was the one message you could not date. */
  time?: string
  createdAt?: string
  status?: string
  /** Starred, so the meta row can say so — see `BubbleMeta`. */
  isStarred?: boolean
  /** Edit time, so the meta row can mark a rewritten caption. */
  editedAt?: string | null
  /** A broadcast's copy, so the meta row can carry the megaphone. */
  viaBroadcast?: boolean
  name: string
  size?: number
  isSentByMe: boolean
  onView?: () => void
  url?: string
  /** Still uploading: the ring sits on the file's icon. */
  upload?: OutboxEntry
}

function FileBubble({ name, size, isSentByMe, onView, url: rawUrl, time, createdAt, status, isStarred, editedAt, viaBroadcast, upload }: FileBubbleProps) {
  // Nothing is fetched for a document bubble. The thumbnail this used to
  // resolve fed an inline preview that no longer exists, and the file's own
  // bytes are the download's business, not the card's.
  const ext = name.split(".").pop()?.toUpperCase() ?? "FILE"
  const isPdf = ext === "PDF"
  const isDoc = ["DOC", "DOCX"].includes(ext)
  const isXls = ["XLS", "XLSX"].includes(ext)

  // What the viewer can actually show. Wiring the click for every document
  // would put a pointer cursor on a file it cannot open and then do nothing
  // when it was clicked; leaving it on PDFs alone — which is where this
  // started — made Word documents and spreadsheets dead cards.
  const VIEWABLE = ["PDF", "DOCX", "DOC", "TXT", "LOG", "MD", "CSV"]
  // Not while it uploads: there is nothing stored to open yet.
  const openDocument = !upload && VIEWABLE.includes(ext) ? onView : undefined

  const iconBg = isPdf ? "bg-red-500" : isDoc ? "bg-blue-500" : isXls ? "bg-green-500" : "bg-slate-500"
  const dividerClass = isSentByMe ? "bg-primary-foreground/15" : "bg-border/60"
  const rowClass = isSentByMe ? "bg-primary-foreground/10 text-primary-foreground/80 hover:bg-primary-foreground/20" : "bg-muted/40 text-foreground hover:bg-muted"

  // No inline preview. A document renders as its icon and its name, and
  // opens in the viewer when it is clicked — a page-one thumbnail sitting in
  // the thread was a preview nobody asked for, in a place too small to read,
  // pushing the rest of the conversation off screen.
  return (
    <div className={"w-[260px] overflow-hidden"}>
      {/* File info row — the whole card opens the document.
          
          Only the small "View" button in the action row did, and that button
          renders for PDFs alone, so tapping the body of a document card was a
          dead area. Tapping a file to open it is what everything else in the
          thread does. */}
      <div
        className={`flex items-center gap-3 p-3 ${openDocument ? "cursor-pointer" : ""}`}
        onClick={openDocument}
      >
        <div className={`relative w-11 h-11 rounded-xl flex flex-col items-center justify-center shrink-0 overflow-hidden ${iconBg}`}>
          <FileText className="w-4.5 h-4.5 text-white" />
          <span className="text-[8px] text-white/90 font-bold mt-0.5 leading-none">{ext}</span>
          {upload && (
            <span className="absolute inset-0 grid place-items-center bg-black/35">
              <UploadControl upload={upload} size={36} />
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold truncate leading-snug ${isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>{name}</p>
          <div className="mt-0.5 flex items-end gap-2">
            <p className={`min-w-0 flex-1 text-[10px] tabular-nums ${isSentByMe ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
              {size ? uploadSizeLabel(size, upload) : ""}{size && ext ? " · " : ""}{ext}
            </p>
            <BubbleMeta time={time} createdAt={createdAt} status={status} isSentByMe={isSentByMe} isStarred={isStarred} editedAt={editedAt} viaBroadcast={viaBroadcast} />
          </div>
        </div>
      </div>
      {/* Action row — only when there is an action.
          
          There is no "View": the card itself opens the document, so that was a
          second control for the same thing sitting directly under the first.
          Download is the recipient's alone, as on the photo and video bubbles —
          the sender picked this file off their own disk and still has it. On
          the sender's own card that leaves nothing to show, and the divider
          and empty strip that used to render anyway read as a control that had
          failed to load.
          
          `downloadAttachment` rather than `<a download>`: the attribute is
          ignored cross-origin, and these URLs sit behind bearer auth, so the
          new tab it opened instead arrived without a token and got a 401. */}
      {!isSentByMe && (
        <>
          <div className={`h-px ${dividerClass}`} />
          <div className="flex">
            <button
              type="button"
              onClick={e => { e.stopPropagation(); if (rawUrl) void downloadAttachment(rawUrl, name) }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium transition-colors ${rowClass}`}
            >
              <Download className="w-3.5 h-3.5" /> Download
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function LocationBubble({ isSentByMe }: { isSentByMe: boolean }) {
  return (
    <div className={"w-[260px] overflow-hidden"}>
      {/* Map placeholder */}
      <div className="relative h-32 bg-muted overflow-hidden">
        <img
          src="https://maps.googleapis.com/maps/api/staticmap?center=37.7749,-122.4194&zoom=15&size=400x200&markers=color:red%7C37.7749,-122.4194&key=DEMO"
          alt="map"
          className="w-full h-full object-cover"
          onError={e => { (e.target as HTMLImageElement).style.display = "none" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-green-100 dark:from-blue-950/40 dark:to-green-950/40 flex items-center justify-center">
          <div className="flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center shadow-lg">
              <MapPin className="w-5 h-5 text-white fill-white" />
            </div>
            <div className="w-3 h-1.5 bg-black/20 rounded-full blur-sm" />
          </div>
        </div>
      </div>
      <div className={`flex items-center gap-2 px-3 py-2 ${isSentByMe ? "bg-primary-foreground/10" : ""}`}>
        <MapPin className="w-3.5 h-3.5 text-red-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold truncate ${isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>Current Location</p>
          <p className={`text-[10px] truncate ${isSentByMe ? "text-primary-foreground/60" : "text-muted-foreground"}`}>San Francisco, CA</p>
        </div>
        <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="shrink-0">
          <ExternalLink className={`w-3.5 h-3.5 ${isSentByMe ? "text-primary-foreground/60" : "text-muted-foreground"}`} />
        </a>
      </div>
    </div>
  )
}

function ContactBubble({ isSentByMe }: { isSentByMe: boolean }) {
  return (
    <div className={"w-[260px] overflow-hidden"}>
      <div className="flex items-center gap-3 p-3">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${isSentByMe ? "bg-primary-foreground/20" : "bg-primary/20"}`}>
          <User className={`w-5 h-5 ${isSentByMe ? "text-primary-foreground" : "text-primary"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold truncate ${isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>Jordan Lee</p>
          <p className={`text-[10px] mt-0.5 ${isSentByMe ? "text-primary-foreground/60" : "text-muted-foreground"}`}>+1 (415) 555-0198</p>
        </div>
      </div>
      <div className={`h-px ${isSentByMe ? "bg-primary-foreground/15" : "bg-border/60"}`} />
      <button className={`w-full flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium transition-colors ${isSentByMe ? "text-primary-foreground/80 hover:bg-primary-foreground/10" : "text-primary hover:bg-muted/40"}`}>
        <User className="w-3.5 h-3.5" /> View Contact
      </button>
    </div>
  )
}

function LinkPreviewCard({ preview, isSentByMe }: { preview: NonNullable<Message["linkPreview"]>; isSentByMe: boolean }) {
  return (
    <a href={preview.url} target="_blank" rel="noopener noreferrer"
      className={`flex flex-col mt-2 rounded-xl overflow-hidden border max-w-[240px] hover:opacity-90 transition-opacity ${isSentByMe ? "border-primary-foreground/20" : "border-border"}`}
    >
      {preview.image && <img src={preview.image} alt="" className="w-full h-20 object-cover" />}
      <div className={`px-2.5 py-1.5 ${isSentByMe ? "bg-primary-foreground/10" : "bg-muted/60"}`}>
        <p className="text-xs font-semibold leading-tight truncate">{preview.title}</p>
        {preview.description && (
          <p className={`text-[10px] mt-0.5 line-clamp-2 ${isSentByMe ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{preview.description}</p>
        )}
        <div className={`flex items-center gap-1 mt-1 ${isSentByMe ? "text-primary-foreground/50" : "text-muted-foreground/60"}`}>
          <ExternalLink className="w-2.5 h-2.5" />
          <span className="text-[9px] truncate">{preview.url}</span>
        </div>
      </div>
    </a>
  )
}

function ReactionsRow({ reactions, isSentByMe, onReact }: { reactions: NonNullable<Message["reactions"]>; isSentByMe: boolean; onReact: (emoji: string) => void }) {
  return (
    <div className={`flex flex-wrap gap-1 mt-1 ${isSentByMe ? "justify-end mr-1" : "justify-start ml-9"}`}>
      {reactions.map(r => (
        <button key={r.emoji} onClick={() => onReact(r.emoji)}
          className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] border transition-all active:scale-95 ${r.byMe ? "bg-primary/10 border-primary/40 text-primary" : "bg-card border-border text-muted-foreground hover:bg-muted"}`}
        >
          <span>{r.emoji}</span><span className="font-semibold">{r.count}</span>
        </button>
      ))}
    </div>
  )
}

function QuickReactionPicker({ onReact, onClose }: { onReact: (e: string) => void; onClose: () => void }) {
  return (
    <div className="flex items-center gap-0.5">
      {QUICK_REACTIONS.map(emoji => (
        <button key={emoji} onClick={() => { onReact(emoji); onClose() }}
          className="text-lg w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-all active:scale-90"
        >{emoji}</button>
      ))}
    </div>
  )
}

/**
 * The react-emoji trigger, standing on its own beside the bubble now rather
 * than sharing a row with the dropdown inside it.
 *
 * `self-center`: its row is `items-end` (so the bubble and, on the received
 * side, the avatar sit on the message's baseline), but the react button reads
 * better centred against the card than pinned to its foot.
 *
 * The picker opens below the button in a portal, outside the thread's scroll
 * container, so on the first message of a chat (nothing above it) it neither
 * gets clipped nor pushes the layout upward.
 */
function ReactButton({ align, reactionPickerOpen, onReactionPickerOpenChange, onReact }: {
  align: "start" | "end"
  reactionPickerOpen: boolean; onReactionPickerOpenChange: (open: boolean) => void; onReact: (emoji: string) => void
}) {
  return (
    <Popover open={reactionPickerOpen} onOpenChange={onReactionPickerOpenChange}>
      <PopoverTrigger asChild>
        <button className="self-center opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-muted/80 transition-all">
          <Smile className="w-3 h-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align={align} sideOffset={6} className="w-auto rounded-full p-1.5">
        <QuickReactionPicker onReact={onReact} onClose={() => onReactionPickerOpenChange(false)} />
      </PopoverContent>
    </Popover>
  )
}

function BubbleMenu({ m, align, onReply, onForward, onCopy, onStar, onPin, onDelete, onEdit, onSelectFromMenu, onInfo, pointerMenu }: {
  m: Message; align: "start" | "end"
  onReply?: () => void; onForward?: () => void; onCopy?: () => void; onStar?: () => void; onPin?: () => void; onDelete?: () => void; onEdit?: () => void
  onSelectFromMenu?: () => void; onInfo?: () => void
  /** Right-click / two-finger-click state, shared with the parent bubble so it
   *  can attach the `contextmenu` handler to the whole card, not just this
   *  trigger button. */
  pointerMenu: PointerMenuState
}) {
  // CDT-5c — Edit + Delete are sender-only (server enforces NOT_SENDER).
  // Edit additionally requires `type === 'text'` (server: CANNOT_EDIT_TYPE),
  // not deleted, and still inside the edit window. Same rule `useEditMessage`
  // checks before saving, so the menu never offers something saving refuses.
  const canEditUi = isEditable(m)
  // Offered on every message, not just your own: removing something from your
  // own copy of a thread is nobody else's business, and it is the commoner of
  // the two deletes. The dialog is what decides scope: your own message can be
  // retracted for everyone (within the time limit); anybody else's, and a
  // message already retracted for everyone, can only be dropped for you.
  const isDeletedUi = !!m.isDeleted
  // Only when the bubble has something typed in it: a plain text message, or
  // the caption on an attachment that actually shows one. Not an
  // attachment-only send, a retracted message, or a system/call pill.
  const canCopy = hasCopyableText(m)
  // Reply and Edit move the user to the message box, which focuses itself.
  // A closing menu hands focus back to its trigger by default, which would
  // pull it straight off the box, so for those two the hand-back is skipped.
  // Shared by both menus below: whichever one a click came from hands off
  // focus the same way.
  const focusHandedOffRef = useRef(false)
  const handOffFocus = (action?: () => void) => () => {
    focusHandedOffRef.current = true
    action?.()
  }
  const onCloseAutoFocus = (e: Event) => {
    if (!focusHandedOffRef.current) return
    focusHandedOffRef.current = false
    e.preventDefault()
  }

  // Built once and rendered into both menus, so a right-click and the
  // three-dot button open the identical actions, confirm dialogs and toasts.
  // A deleted message has no content left for any of these to act on, so it
  // gets only Delete (drop it from your own view). Select was offered too, but
  // there is nothing left to select it for.
  const menuItems = (
    <>
      {!isDeletedUi && (
        <>
          <DropdownMenuItem onClick={onSelectFromMenu}><CheckSquare className="w-4 h-4 mr-2" />Select</DropdownMenuItem>
          <DropdownMenuSeparator />
          {onInfo && <DropdownMenuItem onClick={onInfo}><Info className="w-4 h-4 mr-2" />Message info</DropdownMenuItem>}
          <DropdownMenuItem onClick={handOffFocus(onReply)}><Reply className="w-4 h-4 mr-2" />Reply</DropdownMenuItem>
          <DropdownMenuItem onClick={onForward}><Forward className="w-4 h-4 mr-2" />Forward</DropdownMenuItem>
          {canCopy && <DropdownMenuItem onClick={onCopy}><Copy className="w-4 h-4 mr-2" />Copy</DropdownMenuItem>}
          {canEditUi && <DropdownMenuItem onClick={handOffFocus(onEdit)}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>}
          <DropdownMenuItem onClick={onStar}><Star className={`w-4 h-4 mr-2 ${m.isStarred ? "fill-amber-400 text-amber-400" : ""}`} />{m.isStarred ? "Unstar" : "Star"}</DropdownMenuItem>
          <DropdownMenuItem onClick={onPin}><Pin className="w-4 h-4 mr-2" />{m.isPinned ? "Unpin" : "Pin"}</DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuItem onClick={onDelete} variant="destructive"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
    </>
  )

  return (
    <>
      {/* Anchored to its own trigger at the bubble's top corner and aligned to
          the bubble's inner edge (`align`), so it opens beside the message it
          acts on. The content portals out of the thread's scroll area and
          flips to stay fully on screen for messages at the top or bottom of
          the chat. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-muted/80 transition-all">
            <MoreVertical className="w-3 h-3 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-44" onCloseAutoFocus={onCloseAutoFocus}>
          {menuItems}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Right-click / two-finger-click menu, opened at the pointer instead of
          the trigger button above. Same items, same focus hand-off. */}
      {pointerMenu.point && (
        <DropdownMenu open={pointerMenu.open} onOpenChange={pointerMenu.setOpen}>
          <DropdownMenuTrigger asChild>
            <PointerMenuAnchor point={pointerMenu.point} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align={align} className="w-44" onCloseAutoFocus={onCloseAutoFocus}>
            {menuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  )
}

/** One tile of a multi-photo message. Its own component so the src resolver,
 *  which is a hook, can run per picture. */
function AlbumTile({
  media,
  more,
  onOpen,
  className = "aspect-square",
}: {
  media: MessageMedia
  /** Painted over the last tile when the message holds more than we show. */
  more?: number
  onOpen: () => void
  /** Shape of this tile in the grid — the feature tile is taller. */
  className?: string
}) {
  const { src, status, retry } = useAttachmentSrcState(media.url)
  // A plain URL (no auth fetch) reports `ready` straight away even though the
  // `<img>` itself hasn't loaded yet, so a 404 on one of those only ever shows
  // up as `onError` below, never as `status`. Reset whenever the resolved src
  // changes so a retry (or scrolling to a different tile reusing this slot
  // via key) doesn't keep yesterday's failure.
  const [imgFailed, setImgFailed] = useState(false)
  const [retryNonce, setRetryNonce] = useState(0)
  useEffect(() => setImgFailed(false), [src])

  const failed = status === "failed" || imgFailed

  if (failed) {
    return (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); setImgFailed(false); setRetryNonce(n => n + 1); retry() }}
        className={`relative flex items-center justify-center overflow-hidden bg-muted/50 ${className}`}
      >
        <ImageOff className="h-4 w-4 text-muted-foreground" />
      </button>
    )
  }

  return (
    <button type="button" onClick={onOpen} className={`relative block overflow-hidden ${status === "loading" ? "animate-pulse bg-muted/50" : ""} ${className}`}>
      {src && (
        <img
          key={retryNonce}
          src={src}
          alt={media.name ?? "image"}
          className="w-full h-full object-cover"
          onError={() => setImgFailed(true)}
        />
      )}
      {more ? (
        <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-white text-lg font-medium">
          +{more}
        </span>
      ) : null}
    </button>
  )
}

export function MessageBubble({ message: m, showAvatar = true, isGroup = false, isFirst = true, isLast = true, onReply, onForward, onCopy, onStar, onPin, onDelete, onEdit, onReact, onImageClick, onFileClick, sentBubbleColor, isSelectMode = false, isSelected = false, onSelect, onSelectFromMenu, onInfo, onJumpToMessage, onInviteLinkClick }: MessageBubbleProps) {
  const [showReactionPicker, setShowReactionPicker] = useState(false)
  // Called unconditionally, before any early return, so hook order is stable
  // across message types. Only the image branch reads it.
  const media = useAutoDownload(m.type, m.attachmentUrl, m.isSentByMe)
  // Same resolver as the other bubbles: the image branch below paints an <img>
  // straight from the attachment URL, which 401s on the local storage backend.
  // The "state" variant over the plain one: a failed fetch and a still-loading
  // one both used to hand back `undefined`, so a photo that would never load
  // showed the same spinner forever with no way to tell it apart from one
  // still on its way.
  const { src: imageSrc, status: imageStatus, retry: retryImage } = useAttachmentSrcState(m.attachmentUrl)
  // A plain https URL (no auth fetch, so `imageStatus` is `ready` immediately)
  // can still 404 once the `<img>` itself tries to load it: that only shows
  // up as `onError`, never as `imageStatus`. `imgRetryNonce` is swapped in as
  // the `<img>`'s key on retry so the browser is forced to issue the request
  // again instead of reusing whatever it cached for the failed one.
  const [imgLoadFailed, setImgLoadFailed] = useState(false)
  const [imgRetryNonce, setImgRetryNonce] = useState(0)
  useEffect(() => setImgLoadFailed(false), [imageSrc])
  // A file message of mine still on its way (services/upload-outbox.ts): its
  // media carries the upload ring, and a failed one says "Not sent". Until it
  // has gone there is no message on the server to reply to, forward, star or
  // delete, so it offers none of that — the ✗ on the ring stops it, and the
  // Delete under a stopped or failed one takes it away.
  const upload = useOutboxEntry(m.id)
  // Failed shows the red mark; stopped shows no tick at all — not the clock,
  // which would say it is still on its way.
  const status = upload?.state === "failed" || upload?.state === "stopped" ? upload.state : m.status
  // Called unconditionally too, for the same reason as the two hooks above.
  // Off in select mode only, since the tap already means something else
  // there. A retracted message still opens it: Select and Delete both remain
  // live actions on it.
  const pointerMenu = usePointerMenu(isSelectMode || !!upload)
  // Every picture on this message. One is the ordinary case and keeps the
  // single-image bubble below; more than one becomes an album.
  const photos = (m.attachments ?? []).filter(
    (a) => a.url && (a.kind ? a.kind === "photo" : m.type === "image"),
  )

  // Hover affordances: the react button and the bubble menu.
  //
  // The react button is hidden on a retracted message: `Message.isDeleted` is
  // documented as "only the placeholder sentence survives", so there is
  // nothing left to react to. The bubble menu stays, but BubbleMenu narrows
  // itself to Select and Delete (for me) on a retracted message, the same two
  // actions the right-click menu above offers, so it can still be picked in a
  // bulk selection or dropped from your own view.
  const showActions = !isSelectMode && !m.isDeleted && !upload
  const showMenu = !isSelectMode && !upload

  // System event pill — centered, muted, no avatar/bubble/ticks.
  if (m.type === "system") {
    if (!m.text) return null
    return (
      <div className="flex justify-center my-2">
        <span className="max-w-[80%] text-center text-[11px] leading-snug text-muted-foreground bg-muted/60 border border-border/40 px-3 py-1 rounded-full">
          {m.text}
        </span>
      </div>
    )
  }

  // Call pill
  if (m.isCall || m.isMissedCall) {
    const isMissed = m.isMissedCall
    const isVideo = m.isVideoCall
    const isOut = m.callDir === "out"
    const CallIcon = isMissed
      ? PhoneMissed
      : isOut
        ? PhoneOutgoing
        : isVideo ? Video : PhoneIncoming

    return (
      <div className="flex justify-center my-2">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs border shadow-sm ${isMissed ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40 text-red-600 dark:text-red-400" : "bg-muted border-border/50 text-muted-foreground"}`}>
          <CallIcon className={`w-3.5 h-3.5 shrink-0 ${isMissed ? "text-red-500" : ""}`} />
          <span className="font-medium">{m.text}</span>
          <span className="opacity-60">· {m.time}</span>
        </div>
      </div>
    )
  }

  // Grouping-aware bubble radii
  const sentRadius = `rounded-2xl ${isFirst ? "rounded-br-sm" : ""} ${!isLast ? "rounded-tr-sm" : ""}`
  const recvRadius = `rounded-2xl ${isFirst ? "rounded-bl-sm" : ""} ${!isLast ? "rounded-tl-sm" : ""}`

  // file/video/location/contact own their bg; image needs bubble bg (no padding) so caption text is readable
  const isRichType = ["video", "file", "location", "contact"].includes(m.type ?? "text")
  const isImageType = m.type === "image"
  /** Renders as a card in a bubble: photo, video, document, location, contact.
   *  They all get the bubble, so they all get the sent/received colour. */
  const isMediaCard = isRichType || isImageType

  const renderContent = (isSentByMe: boolean) => {
    // Every other bubble shows when it was sent; a photo showed nothing,
    // loaded or not, so a viewer had no way to tell when it arrived. Shared by
    // all three photo shapes — loaded, still behind its placeholder, and an
    // album — because the first fix covered only the loaded single photo.
    const photoMeta = (
      <div className="flex justify-end px-3.5 pb-2 pt-1">
        <BubbleMeta time={m.time} createdAt={m.createdAt} status={status} isSentByMe={isSentByMe} isStarred={m.isStarred} viaBroadcast={m.viaBroadcast} />
      </div>
    )
    if (m.type === "image" && photos.length > 1) {
      // Several pictures, one message. Opening a tile hands the viewer that
      // picture by moving it into the singular fields it reads.
      const { shown: tileCount, hidden, shape } = albumLayout(photos.length)
      const shown = photos.slice(0, tileCount)
      // Three pictures in a 2x2 leave a hole, so the first one takes the full
      // height beside the other two.
      const gridClass =
        shape === "feature"
          ? "grid grid-cols-2 grid-rows-2 aspect-square"
          : shape === "single"
            ? "grid grid-cols-1"
            : "grid grid-cols-2"
      const tileClass = (i: number) =>
        shape === "feature" ? (i === 0 ? "row-span-2 h-full" : "h-full") : "aspect-square"
      return (
        <div>
          {media.shouldLoad ? (
            <div className={`relative gap-0.5 max-w-[260px] ${gridClass}`}>
              {/* Out of flow, so the grid lays out the tiles as if it were not there. */}
              {upload && <MediaUploadOverlay upload={upload} />}
              {shown.map((a, i) => (
                <AlbumTile
                  key={a.url + i}
                  media={a}
                  className={tileClass(i)}
                  more={i === shown.length - 1 && hidden > 0 ? hidden : undefined}
                  onOpen={() =>
                    onImageClick?.({
                      ...m,
                      attachmentUrl: a.url,
                      attachmentName: a.name,
                      attachmentSize: a.size,
                      attachmentMime: a.mime,
                      attachmentThumbnailUrl: a.thumbnailUrl,
                      attachmentWidth: a.width,
                      attachmentHeight: a.height,
                    })
                  }
                />
              ))}
            </div>
          ) : (
            <MediaPlaceholder
              label={`${photos.length} photos`}
              size={photos.reduce((sum, a) => sum + (a.size ?? 0), 0) || undefined}
              preview={photos[0].thumbnailUrl}
              onLoad={media.load}
              isSentByMe={isSentByMe}
            />
          )}
          {m.text && (
            <p className={`text-sm px-3.5 pt-2 ${isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>
              <TextWithMentionChips text={m.text} isSentByMe={isSentByMe} onInviteLinkClick={onInviteLinkClick} />
            </p>
          )}
          {photoMeta}
        </div>
      )
    }
    if (m.type === "image" && m.attachmentUrl) {
      if (!media.shouldLoad) {
        return (
          <div>
            <MediaPlaceholder
              label="Photo"
              size={m.attachmentSize}
              width={m.attachmentWidth}
              height={m.attachmentHeight}
              preview={m.attachmentThumbnailUrl}
              onLoad={media.load}
              isSentByMe={isSentByMe}
              box={singlePhotoBox(m.attachmentWidth, m.attachmentHeight)}
            />
            {m.text && (
              <p className={`text-sm px-3.5 pt-2 ${isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>
                <TextWithMentionChips text={m.text} isSentByMe={isSentByMe} onInviteLinkClick={onInviteLinkClick} />
              </p>
            )}
            {photoMeta}
          </div>
        )
      }
      // A failed fetch (auth URL) or a failed `<img>` load (plain URL that
      // 404s) both land here. They used to be indistinguishable from "still
      // loading": `useAttachmentSrc` handed back `undefined` for both, so a
      // photo that would never arrive spun forever with nothing on screen
      // saying so and no way to retry short of reloading the thread.
      if (imageStatus === "failed" || imgLoadFailed) {
        const box = singlePhotoBox(m.attachmentWidth, m.attachmentHeight)
        return (
          <div>
            <FailedPhotoBox
              box={box}
              isSentByMe={isSentByMe}
              onRetry={() => {
                setImgLoadFailed(false)
                setImgRetryNonce(n => n + 1)
                retryImage()
              }}
            />
            {m.text && (
              <p className={`text-sm px-3.5 pt-2 ${isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>
                <TextWithMentionChips text={m.text} isSentByMe={isSentByMe} onInviteLinkClick={onInviteLinkClick} />
              </p>
            )}
            <div className="flex justify-end px-3.5 pb-2 pt-1">
              <BubbleMeta time={m.time} createdAt={m.createdAt} status={status} isSentByMe={isSentByMe} isStarred={m.isStarred} viaBroadcast={m.viaBroadcast} />
            </div>
          </div>
        )
      }
      // The bytes are still resolving (a bearer-authed fetch behind
      // `useAttachmentSrcState` on the local storage backend). Rendering
      // `<img>` with no src in the meantime draws a broken-image icon, which
      // reads as "this file is broken" rather than "this is loading": a
      // received photo's first moment on screen was exactly that broken icon,
      // on every single one, for however long the fetch took.
      if (!imageSrc) {
        const box = singlePhotoBox(m.attachmentWidth, m.attachmentHeight)
        return (
          <div>
            <div
              style={box}
              className={`flex items-center justify-center ${isSentByMe ? "bg-primary-foreground/10" : "bg-muted/50"}`}
            >
              <div className={`h-6 w-6 animate-spin rounded-full border-2 ${isSentByMe ? "border-primary-foreground/30 border-t-primary-foreground/80" : "border-muted-foreground/25 border-t-muted-foreground/70"}`} />
            </div>
            {m.text && (
              <p className={`text-sm px-3.5 pt-2 ${isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>
                <TextWithMentionChips text={m.text} isSentByMe={isSentByMe} onInviteLinkClick={onInviteLinkClick} />
              </p>
            )}
            <div className="flex justify-end px-3.5 pb-2 pt-1">
              <BubbleMeta time={m.time} createdAt={m.createdAt} status={status} isSentByMe={isSentByMe} isStarred={m.isStarred} viaBroadcast={m.viaBroadcast} />
            </div>
          </div>
        )
      }
      // Fixing the image to the same box the placeholder and spinner used
      // (only possible once we know the real dimensions) is what makes the
      // three states land in the same spot instead of the bubble jumping as
      // the photo resolves. Unknown dimensions keep the old auto-height img,
      // same as before this fix.
      const loadedBox = m.attachmentWidth && m.attachmentHeight ? singlePhotoBox(m.attachmentWidth, m.attachmentHeight) : undefined
      return (
        <div>
          {/* Image fills the bubble (parent has overflow-hidden + rounding) */}
          <div className="relative max-w-[260px]">
            {upload && <MediaUploadOverlay upload={upload} />}
            <button type="button" onClick={() => onImageClick?.(m)} className="block w-full">
              <img
                key={imgRetryNonce}
                src={imageSrc}
                alt={m.attachmentName ?? "image"}
                style={loadedBox ? { height: loadedBox.height } : undefined}
                className="w-full h-auto object-cover max-h-52"
                // Catches a plain URL that resolves (no auth fetch to fail)
                // but then 404s once the browser actually requests it:
                // `imageStatus` alone can't see that, only the element can.
                onError={() => setImgLoadFailed(true)}
              />
            </button>
            {/* No download in the corner — see the video bubble. The viewer
                this opens carries it, and only for the recipient. */}
            {m.attachmentSize && (
              <span className={`absolute bottom-2 left-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full tabular-nums ${upload ? "z-10" : ""}`}>
                {uploadSizeLabel(m.attachmentSize, upload)}
              </span>
            )}
          </div>
          {m.text && (
            <p className={`text-sm px-3.5 pt-2 ${isSentByMe ? "text-primary-foreground" : "text-foreground"}`}>
              <TextWithMentionChips text={m.text} isSentByMe={isSentByMe} onInviteLinkClick={onInviteLinkClick} />
            </p>
          )}
          {photoMeta}
        </div>
      )
    }
    // Attachments carry captions too. The image branch above has always shown
    // one; these three dropped it, so text typed alongside a document, a video
    // or an audio file was uploaded, sent, stored — and then never rendered.
    // The sender saw their words vanish and had no way to tell whether the
    // recipient had them.
    //
    // The colour is chosen from where the caption actually SITS, not from who
    // sent it. A rich bubble (video, document, location, contact) has no
    // background — the class list is the radius and nothing else — so its
    // caption is drawn on the thread background. Using the on-primary colour
    // there put white text on a white page: the sender's words were sent,
    // stored and rendered, and were invisible to them. Audio is not a rich
    // type and does keep the coloured bubble, so it keeps the paired colour.
    const captionClass = isSentByMe ? "text-primary-foreground" : "text-foreground"
    const withCaption = (bubble: React.ReactNode) =>
      m.text ? (
        <div className="flex flex-col gap-1">
          {bubble}
          {/* Lined up with the card above it. The document card's icon starts
              at its `p-3` inset and the caption sat at `px-1`, so the text
              began eight pixels to the left of everything it belonged to and
              read as a separate stray line rather than part of the card. */}
          <p className={`px-3 pb-2 text-sm ${captionClass}`}>
            <TextWithMentionChips text={m.text} isSentByMe={isSentByMe} onInviteLinkClick={onInviteLinkClick} />
          </p>
        </div>
      ) : (
        bubble
      )

    if (m.type === "video") return withCaption(<VideoBubble url={m.attachmentUrl} name={m.attachmentName} size={m.attachmentSize} poster={m.attachmentThumbnailUrl} isSentByMe={isSentByMe} onOpen={() => onImageClick?.(m)} upload={upload} />)
    if (m.type === "audio") return withCaption(<AudioBubble url={m.attachmentUrl} duration={m.audioDuration} isSentByMe={isSentByMe} time={m.time} createdAt={m.createdAt} status={status} isStarred={m.isStarred} editedAt={m.editedAt} viaBroadcast={m.viaBroadcast} upload={upload} />)
    if (m.type === "file" && m.attachmentName) return withCaption(<FileBubble name={m.attachmentName} size={m.attachmentSize} url={m.attachmentUrl} isSentByMe={isSentByMe} onView={() => onFileClick?.(m)} time={m.time} createdAt={m.createdAt} status={status} isStarred={m.isStarred} editedAt={m.editedAt} viaBroadcast={m.viaBroadcast} upload={upload} />)
    if (m.type === "location") return <LocationBubble isSentByMe={isSentByMe} />
    if (m.type === "contact") return <ContactBubble isSentByMe={isSentByMe} />
    return (
      <>
        {m.replyTo && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onJumpToMessage?.(m.replyTo!.id) }}
            title="Go to the replied message"
            className={`mb-1.5 block w-full cursor-pointer rounded-r pl-2 text-left text-xs line-clamp-2 transition-colors ${isSentByMe ? "border-l-2 border-primary-foreground/40 text-primary-foreground/70 hover:bg-primary-foreground/10" : "border-l-2 border-primary/50 text-muted-foreground hover:bg-muted"}`}
          >
            <span className={`font-semibold block ${isSentByMe ? "text-primary-foreground" : "text-primary"}`}>{m.replyTo.senderName}</span>
            {m.replyTo.text}
          </button>
        )}
        {/* Flex-wrap, so the meta is a sibling of the text rather than inline
            content trailing it.
            Inline, it had no break opportunity after the final word — the meta
            is `whitespace-nowrap` — so a full-width line shoved it past the
            right edge. Floated, it left the bubble too narrow for a
            one-character message and the meta hung off the left. As a flex item
            it wraps to its own line when it does not fit, and counts toward the
            bubble's width when it does. `ml-auto` keeps it against the right
            edge on whichever line it lands, which is what the float was for. */}
        <div className="flex flex-wrap items-end gap-x-2">
          {/* `overflow-wrap: anywhere`, not just `break-word`.
              They break a long run identically ONCE the line box has a width —
              the difference is what they report as the element's min-content
              size. `break-word` reports the unbroken run, so a flex item
              containing one is still sized to fit it whole and the bubble grows
              past its column instead of wrapping. `anywhere` reports the
              broken size, which is what lets the bubble shrink.

              It matters here because a message with no spaces in it is a single
              token: `TextWithMentionChips` splits on whitespace, so the whole
              message lands in one span with no break opportunity of its own. */}
          <span className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
            <TextWithMentionChips text={m.text} isSentByMe={isSentByMe} onInviteLinkClick={onInviteLinkClick} />
          </span>
          <BubbleMeta time={m.time} createdAt={m.createdAt} status={status} isSentByMe={isSentByMe} isStarred={m.isStarred} editedAt={m.editedAt} viaBroadcast={m.viaBroadcast} />
        </div>
        {m.linkPreview && <LinkPreviewCard preview={m.linkPreview} isSentByMe={isSentByMe} />}
      </>
    )
  }

  // A deleted message has nothing left to forward, star or copy, so select
  // mode skips it: no checkbox (a spacer keeps the rows aligned) and no click.
  const selectable = isSelectMode && !m.isDeleted && !upload
  const SelectCheckbox = () => !selectable ? <div className="w-5 h-5 shrink-0" /> : (
    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${isSelected ? "bg-primary border-primary shadow-sm" : "border-muted-foreground/70 bg-background shadow-sm"}`}>
      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
    </div>
  )

  if (m.isSentByMe) {
    return (
      <div
        className={`flex items-end gap-2 mb-0.5 w-full transition-colors ${isSelectMode ? "cursor-pointer" : ""} ${isSelected ? SELECTED_ROW : ""}`}
        onClick={selectable ? () => onSelect?.(m.id) : undefined}
      >
        {/* Checkbox always on far left */}
        {isSelectMode && <SelectCheckbox />}

        {/* Bubble column — pushed to right */}
        <div className={`flex flex-col items-end ${isSelectMode ? "flex-1" : "w-full"}`}>
          {m.isPinned && !isSelectMode && <div className="flex items-center gap-1 mb-0.5 text-[10px] text-primary/70 mr-1"><Pin className="w-2.5 h-2.5" /> Pinned</div>}
          {/* `w-full` so this row has a definite width. Left to shrink-to-fit
              it had none, the bubble column's `max-w-[72%]` had nothing to be a
              percentage OF, and the bubble sized itself to the message instead
              — running off the side of the thread. */}
          <div className="flex w-full justify-end items-end gap-1 group relative">
            {showActions && (
              <ReactButton align="end" reactionPickerOpen={showReactionPicker} onReactionPickerOpenChange={setShowReactionPicker} onReact={(e) => { onReact?.(m, e) }} />
            )}
            <div className={`flex min-w-0 flex-col items-end ${isRichType || isImageType ? "max-w-[280px]" : "max-w-[72%] sm:max-w-[65%]"}`}>
              <div
                className={`relative text-sm shadow-sm ${
                  // Every attachment gets the bubble, not just photos.
                  //
                  // Rich types were given the radius and no background, so a
                  // document's own card carried the colour and the caption
                  // under it sat on the thread — which is the same surface for
                  // both people. A photo with a caption reads as sent or
                  // received at a glance; the same message with a PDF read as
                  // neither. The distinction has to belong to the bubble,
                  // because the caption lives in the bubble.
                  isMediaCard
                    ? `${sentBubbleColor ? "" : "bg-primary"} text-primary-foreground overflow-hidden ${sentRadius}`
                    : `${sentBubbleColor ? "" : "bg-primary"} text-primary-foreground px-3.5 py-2 w-fit max-w-full overflow-hidden ${sentRadius}`
                }`}
                style={sentBubbleColor ? { backgroundColor: sentBubbleColor } : undefined}
                onContextMenu={pointerMenu.onContextMenu}
              >
                {renderContent(true)}
                {/* Bubble clips with `overflow-hidden`, so this has to sit
                    inside its box rather than below it. */}
                {showMenu && (
                  <div className="absolute top-1 right-1">
                    <BubbleMenu m={m} align="end" onReply={() => onReply?.(m)} onForward={() => onForward?.(m)} onCopy={() => onCopy?.(m)} onStar={() => onStar?.(m)} onPin={() => onPin?.(m)} onDelete={() => onDelete?.(m)} onEdit={() => onEdit?.(m)} onSelectFromMenu={() => { onSelectFromMenu?.(m.id) }} onInfo={onInfo ? () => onInfo(m) : undefined} pointerMenu={pointerMenu} />
                  </div>
                )}
              </div>
            </div>
          </div>
          {(upload?.state === "failed" || upload?.state === "stopped") && <UploadHaltedStrip upload={upload} />}
          {!isSelectMode && m.reactions && m.reactions.length > 0 && <ReactionsRow reactions={m.reactions} isSentByMe onReact={(e) => onReact?.(m, e)} />}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`flex items-end gap-2 mb-0.5 w-full transition-colors ${isSelectMode ? "cursor-pointer" : ""} ${isSelected ? SELECTED_ROW : ""}`}
      onClick={selectable ? () => onSelect?.(m.id) : undefined}
    >
      {/* Checkbox always on far left */}
      {isSelectMode && <SelectCheckbox />}

      {/* Bubble column — left-aligned */}
      <div className={`flex flex-col items-start ${isSelectMode ? "flex-1" : "w-full"}`}>
        {m.isPinned && !isSelectMode && <div className="flex items-center gap-1 mb-0.5 ml-9 text-[10px] text-primary/70"><Pin className="w-2.5 h-2.5" /> Pinned</div>}
        {/* Same as the sent side: a definite width for the % max-width below. */}
        <div className="flex w-full items-end gap-2 group relative">
          {isGroup && !isSelectMode && (
            isFirst && showAvatar
              ? <Avatar name={m.senderName ?? "?"} imageUrl={m.senderImageUrl} contactId={m.senderId} />
              : <div className="w-7 shrink-0" />
          )}
          <div className={`flex min-w-0 flex-col ${isRichType || isImageType ? "max-w-[280px]" : "max-w-[72%] sm:max-w-[65%]"}`}>
            {isGroup && isFirst && m.senderName && !isSelectMode && <span className="text-xs font-semibold text-primary mb-0.5 ml-0.5">{m.senderName}</span>}
            <div className={`relative text-sm text-foreground shadow-sm ${
              // See the sent side — attachments all get the bubble.
              isMediaCard
                ? `bg-card dark:bg-muted border border-border/50 overflow-hidden ${recvRadius}`
                : `bg-card dark:bg-muted px-3.5 py-2 w-fit max-w-full border border-border/50 overflow-hidden ${recvRadius}`
            }`}
            onContextMenu={pointerMenu.onContextMenu}
            >
              {renderContent(false)}
              {/* Bubble clips with `overflow-hidden`, so this has to sit
                  inside its box rather than below it. */}
              {showMenu && (
                <div className="absolute top-1 right-1">
                  <BubbleMenu m={m} align="start" onReply={() => onReply?.(m)} onForward={() => onForward?.(m)} onCopy={() => onCopy?.(m)} onStar={() => onStar?.(m)} onPin={() => onPin?.(m)} onDelete={() => onDelete?.(m)} onEdit={() => onEdit?.(m)} onSelectFromMenu={() => { onSelectFromMenu?.(m.id) }} onInfo={onInfo ? () => onInfo(m) : undefined} pointerMenu={pointerMenu} />
                </div>
              )}
            </div>
          </div>
          {showActions && (
            <ReactButton align="start" reactionPickerOpen={showReactionPicker} onReactionPickerOpenChange={setShowReactionPicker} onReact={(e) => { onReact?.(m, e) }} />
          )}
        </div>
        {!isSelectMode && m.reactions && m.reactions.length > 0 && <ReactionsRow reactions={m.reactions} isSentByMe={false} onReact={(e) => onReact?.(m, e)} />}
      </div>
    </div>
  )
}
